<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import GwIcon from '@/components/GwIcon.vue'
import { computed, onMounted } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { mosaicAspect, mosaicClass, storefrontTemplates } from '@/data/storefrontTemplates.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'
import { useWebTranslations } from '@/composables/useWebTranslations.js'

const { locale, webRoute, alternateLocales, get } = useWebTranslations()
const { refresh } = useScrollReveal()

const adminUrl = 'https://admin.growiyo.com'

const templates = computed(() =>
  storefrontTemplates.map((t) => ({
    ...t,
    name: get(`static.templates.items.${t.id}.name`),
    industry: get(`static.templates.items.${t.id}.industry`),
    tagline: get(`static.templates.items.${t.id}.tagline`),
    alt: `${get(`static.templates.items.${t.id}.name`)} · ${get(`static.templates.items.${t.id}.industry`)}`,
    span: mosaicClass[t.mosaic],
    aspect: mosaicAspect[t.mosaic],
  }))
)

onMounted(() => {
  refresh()
  const hash = window.location.hash
  if (hash) {
    document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
})

const canonicalPath = computed(() => webRoute('web.templates'))
const pageAlternateLocales = computed(() => alternateLocales('web.templates'))
</script>

<template>
  <SEOHead
    :title="get('static.templates.seo_title')"
    :description="get('static.templates.seo_description')"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <!-- COVER -->
  <section class="relative overflow-hidden isolate gw-paper">
    <div aria-hidden="true" class="absolute inset-0 gw-pattern opacity-40 pointer-events-none"></div>
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <div class="gw-halo-warm" style="top:-6rem; right:-8rem; width:32rem; height:32rem; opacity:0.45;"></div>
      <div class="gw-halo-magenta" style="bottom:-10rem; left:-8rem; width:28rem; height:28rem; opacity:0.4;"></div>
    </div>

    <div class="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-24 pb-12 lg:pt-36 lg:pb-16">
      <div class="reveal grid grid-cols-12 gap-6 items-end">
        <div class="col-span-12 lg:col-span-8">
          <span class="gw-eyebrow">{{ get('static.templates.cover.eyebrow') }}</span>
          <h1 class="gw-display mt-5" style="font-size: clamp(2.75rem, 7vw, 6rem); line-height: 0.92; letter-spacing: -0.035em;">
            <em>{{ get('static.templates.cover.heading_emphasis') }}</em>{{ get('static.templates.cover.heading_rest') }}
          </h1>
        </div>
        <div class="col-span-12 lg:col-span-4 lg:pb-3">
          <p class="gw-lead max-w-[36ch]">
            {{ get('static.templates.cover.lead') }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- MOSAIC -->
  <section class="bg-white relative overflow-x-clip" :aria-label="get('static.templates.mosaic_aria')">
    <div class="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-12 pb-20 lg:pb-28 pt-2">
      <div class="mosaic grid grid-cols-12 gap-3 lg:gap-4">
        <article
          v-for="(t, idx) in templates"
          :id="`template-${t.id}`"
          :key="t.id"
          :class="['mosaic-item group scroll-mt-28', t.span]"
        >
          <a
            :href="adminUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="block relative"
            :aria-label="`${t.name}. ${get('static.templates.create_account_aria')}`"
          >
            <div class="media relative overflow-hidden bg-[var(--gw-paper)] border gw-rule-strong">
              <img
                :src="t.src"
                :alt="t.alt"
                :width="t.width"
                :height="t.height"
                :loading="idx < 2 ? 'eager' : 'lazy'"
                decoding="async"
                :class="['w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.14]', t.aspect]"
              />
              <span
                aria-hidden="true"
                class="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700"
                style="background: var(--gw-gradient);"
              ></span>

              <!-- Hover popup -->
              <div class="popup absolute inset-0 flex flex-col justify-end p-5 lg:p-7">
                <p class="gw-mono text-[10px] uppercase tracking-[0.22em] font-semibold text-white/75">
                  {{ t.industry }}
                </p>
                <h3 class="mt-1.5 text-white italic font-extrabold tracking-[-0.03em] leading-none" style="font-size: clamp(1.5rem, 2.6vw, 2.25rem);">
                  {{ t.name }}
                </h3>
                <p class="mt-2 text-[0.9rem] text-white/85 leading-snug max-w-[36ch]">
                  {{ t.tagline }}
                </p>
                <span class="mt-4 inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[color:var(--gw-ink)] text-[13px] font-semibold tracking-[-0.005em]">
                  {{ get('static.templates.create_account') }}
                  <GwIcon name="arrow-right" class="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </a>
        </article>
      </div>
    </div>
  </section>

  <!-- CLOSING CTA -->
  <section class="gw-paper relative overflow-hidden">
    <div aria-hidden="true" class="absolute inset-0 gw-pattern opacity-40 pointer-events-none"></div>
    <div class="relative max-w-[920px] mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center reveal">
      <h2 class="gw-title">
        <em>{{ get('static.templates.closing.heading_emphasis') }}</em>
        {{ get('static.templates.closing.heading_rest') }}
      </h2>
      <p class="gw-lead mt-7 max-w-[44ch] mx-auto">
        {{ get('static.templates.closing.description') }}
      </p>
      <a :href="adminUrl" target="_blank" rel="noopener noreferrer" class="gw-btn-grad mt-10">
        {{ get('static.templates.closing.cta') }}
        <GwIcon name="arrow-right" class="w-4 h-4" />
      </a>
    </div>
  </section>
</template>

<style scoped>
.mosaic-item {
  position: relative;
  z-index: 1;
}
.mosaic-item:hover,
.mosaic-item:focus-within {
  z-index: 20;
}

.popup {
  background: linear-gradient(180deg, rgba(51,51,51,0) 28%, rgba(51,51,51,0.82) 100%);
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.35s ease, transform 0.35s ease;
  pointer-events: none;
}
.mosaic-item:hover .popup,
.mosaic-item:focus-within .popup {
  opacity: 1;
  transform: translateY(0);
}

@media (hover: none) {
  .popup {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .popup,
  .mosaic-item img {
    transition: none;
  }
  .mosaic-item img {
    transform: none !important;
  }
}
</style>
